<template>
	<div class="space-y-px overflow-hidden rounded-lg">
		<div
			class="bg-fill-secondary text-label-primary flex min-w-0 items-center gap-2.5 px-4 py-3 text-sm font-semibold uppercase"
			v-hover="{ active: 'bg-fill-tertiary', impact: 'soft' }"
			@click="toggleCollapse()"
		>
			<div class="min-w-0 flex-1 truncate" v-if="participants.length > 1">
				{{ t('team', [teamIndex + 1]) }}
			</div>
			<template v-else>
				<div
					v-for="participant in participants"
					class="flex min-w-0 flex-1 items-center gap-2"
				>
					<LazyImage
						v-if="participant.avatar && participant.name"
						:src="avatarSm(participant.avatar)"
						:alt="participant.name"
						class="h-5.5 w-5.5 shrink-0 rounded-full"
					/>
					<LoaderIcon v-else class="text-label-tertiary h-5.5 w-5.5 shrink-0" />
					<div class="min-w-0 truncate">
						{{ participant.name ? participant.name : t('waiting') }}
					</div>
				</div>
			</template>
			<CoinPrice
				:price="currentPrice"
				:minDigits="2"
				mono
				:animationDuration="1000"
			/>
			<div
				v-if="chanceValue !== undefined"
				class="rounded-lg border px-1.5 py-0.5 text-xs font-medium"
				:class="[currentChanceColor]"
			>
				{{ chanceValue.toFixed(2) }}%
			</div>
			<CaratUpIcon
				class="text-label-tertiary h-5 w-5 transition-transform"
				:class="{ 'rotate-180': collapsed }"
			/>
		</div>
		<div class="grid grid-cols-2 gap-px transform-3d" v-if="!collapsed">
			<div
				v-for="(item, i) in casesWon"
				class="relative h-52.5 transition-transform duration-500 transform-3d"
				:class="{
					'rotate-y-180': currentRound > item.roundIndex && item.item,
					'col-span-2': i + 1 === casesWon.length && casesWon.length % 2 === 1
				}"
			>
				<ItemInner
					v-if="item.item"
					class="bg-fill-secondary absolute inset-0 z-0 rotate-y-180 px-3 pt-3 pb-5 backface-hidden"
					:class="{
						'col-span-2': i + 1 === casesWon.length && casesWon.length % 2 === 1
					}"
					:collection="item.item.collectionName"
					:name="item.item.modelName"
					:price="item.item.price"
					:src="icon(item.item.modelUuid)"
					:preloader="item.item.preloader || undefined"
					:color="getItemColor(item.item, item.case.price)"
					@click="openInfo(item.item, item.case)"
				/>
				<div
					class="bg-fill-secondary relative z-1 flex h-full flex-col items-center px-3 pt-3 pb-5 backface-hidden"
				>
					<LazyImage
						:src="caseIcon(item.case.uuid)"
						alt="case"
						class="aspect-square w-24 transition-all duration-500"
						:class="{
							'opacity-60 grayscale-100': currentRound < item.roundIndex
						}"
					/>
					<div class="mt-4 flex w-full flex-1 flex-col justify-center px-3">
						<div
							class="text-label-secondary truncate text-center text-xs font-medium uppercase"
						>
							{{ t('rounds', item.roundIndex + 1) }}
						</div>
						<div
							class="text-label-primary truncate text-center text-sm font-medium uppercase"
						>
							{{ item.case.name }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			class="bg-fill-secondary text-label-primary flex min-w-0 items-center justify-center gap-5 px-4 py-3 text-sm"
			v-if="participants.length > 1"
		>
			<div
				v-for="participant in participants"
				class="flex min-w-0 items-center gap-2"
			>
				<LazyImage
					v-if="participant.avatar && participant.name"
					:src="avatarSm(participant.avatar)"
					:alt="participant.name"
					class="h-5.5 w-5.5 shrink-0 rounded-full"
				/>
				<LoaderIcon v-else class="text-label-tertiary h-5.5 w-5.5 shrink-0" />
				<div class="min-w-0 truncate">
					{{ participant.name ? participant.name : t('waiting') }}
				</div>
			</div>
		</div>
		<ItemTicketsModal
			:chance="dropInfo.item.chance"
			:collectionName="dropInfo.item.collectionName"
			:modelName="dropInfo.item.modelName"
			:modelUuid="dropInfo.item.modelUuid"
			:ticketsRange="dropInfo.item.ticketsRange"
			:price="dropInfo.item.price"
			:preloader="dropInfo.item.preloader"
			v-model="dropInfoModal"
		>
			<CaseTransactionCard
				class="mt-7 w-full"
				:uuid="dropInfo.box.uuid"
				:name="dropInfo.box.name"
				:price="dropInfo.box.price"
			/>
		</ItemTicketsModal>
	</div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CoinPrice from './CoinPrice.vue'
import CaratUpIcon from '@/assets/icons/carat-up.svg'
import LazyImage from './LazyImage.vue'
import LoaderIcon from '@/assets/icons/loader.svg'
import { caseIcon } from '@/utils/urls'
import ItemInner from './ItemInner.vue'
import { icon, avatarSm } from '@/utils/urls'
import { useI18n } from 'vue-i18n'
import ItemTicketsModal from './modals/ItemTicketsModal.vue'
import CaseTransactionCard from './CaseTransactionCard.vue'
import { getItemColor } from '@/utils/itemColor'
import { chance } from '@/utils/chance'

const colors = [
	'border-[#E01533] text-[#E01533]', // red
	'border-[#E4A211] text-[#E4A211]', // yellow
	'border-[#E06315] text-[#E06315]', // orange
	'border-[#84B611] text-[#84B611]' // green
]

const { t } = useI18n({
	messages: {
		en: {
			team: 'team {0}',
			waiting: 'Waiting...',
			rounds: '{n} round | {n} rounds'
		},
		ru: {
			team: 'команда {0}',
			waiting: 'Ожидание...',
			rounds: '{n} раундов | {n} раунд | {n} раунда | {n} раундов'
		}
	}
})

const props = defineProps<{
	participants: {
		avatar: string | null
		name: string | null
		slotIndex: number
	}[]
	cases: string[]
	casesData: Record<
		string,
		{
			name: string
			price: number
		}
	>
	rounds: {
		uuid: string
		results: {
			participant: {
				teamIndex: number
				slotIndex: number
			}
			item?: {
				modelUuid: string
				price: number
				modelName: string
				collectionName: string
				chance: number
				preloader: string | null
				ticketsRange: [number, number]
			}
		}[]
	}[]
	currentRound: number
	teamIndex: number
	isJackpot?: boolean
	isCrazy?: boolean
}>()

const collapsed = ref(true)

const toggleCollapse = () => {
	collapsed.value = !collapsed.value
}

const currentPrice = computed(() =>
	props.rounds
		.slice(0, props.currentRound)
		.reduce(
			(sum, round) =>
				sum +
				round.results
					.filter((r) => r.participant.teamIndex === props.teamIndex)
					.reduce((s, r) => s + (r.item?.price || 0), 0),
			0
		)
)

const currentChance = computed(() => {
	if (!props.isJackpot) {
		return undefined
	}
	if (!props.currentRound || !props.rounds.length) {
		return 0
	}

	const teamTickets: { teamIndex: number; tickets: number }[] = []
	const rounds = props.rounds.slice(0, props.currentRound)

	for (let i = 0; i < rounds.length; i++) {
		const round = rounds[i]
		for (let j = 0; j < round.results.length; j++) {
			const result = round.results[j]
			const findTeam = teamTickets.find(
				(t) => t.teamIndex === result.participant.teamIndex
			)
			if (findTeam) {
				findTeam.tickets += result.item?.price || 0
			} else {
				teamTickets.push({
					teamIndex: result.participant.teamIndex,
					tickets: result.item?.price || 0
				})
			}
		}
	}

	return chance(props.teamIndex, teamTickets, props.isCrazy) * 100
})

const chanceValue = ref<number>()

let start = 0
const createAnimationFrame = (from: number, to: number, duration: number) => {
	start = performance.now()
	const animate = (current: number) => {
		const progress = (current - start) / duration
		chanceValue.value = from + (to - from) * progress
		if (progress < 1) {
			requestAnimationFrame(animate)
		} else {
			chanceValue.value = to
		}
	}
	requestAnimationFrame(animate)
}

watch(
	currentChance,
	(newChance, oldChance) => {
		if (newChance === undefined) {
			chanceValue.value = undefined
			return
		}

		createAnimationFrame(oldChance || 0, newChance, 1000)
	},
	{ immediate: true }
)

const currentChanceColor = computed(() => {
	if (!props.isJackpot) {
		return undefined
	}

	return colors[props.teamIndex]
})

const casesWon = computed(() =>
	props.cases.flatMap((uuid, i) => {
		const round = props.rounds[i]

		const teamResults = round
			? round.results.filter((r) => r.participant.teamIndex === props.teamIndex)
			: Array.from({ length: props.participants.length }).map(() => null)

		return teamResults.map((r) => ({
			roundIndex: i,
			case: {
				uuid,
				name: props.casesData[uuid]?.name,
				price: props.casesData[uuid]?.price || 0
			},
			item: r ? r.item : null
		}))
	})
)

const dropInfo = ref<{
	item: {
		chance: number
		collectionName: string
		modelName: string
		modelUuid: string
		ticketsRange: [number, number]
		price: number
		preloader: string | null
	}
	box: {
		uuid: string
		name: string
		price: number
	}
}>({
	item: {
		chance: 0,
		collectionName: '',
		modelName: '',
		modelUuid: '',
		ticketsRange: [0, 0],
		price: 0,
		preloader: null
	},
	box: {
		uuid: '',
		name: '',
		price: 0
	}
})
const dropInfoModal = ref(false)

const openInfo = (
	item: {
		chance: number
		collectionName: string
		modelName: string
		modelUuid: string
		ticketsRange: [number, number]
		price: number
		preloader: string | null
	},
	box: {
		uuid: string
		name: string
		price: number
	}
) => {
	dropInfo.value = {
		item: {
			chance: item.chance,
			collectionName: item.collectionName,
			modelName: item.modelName,
			modelUuid: item.modelUuid,
			ticketsRange: item.ticketsRange,
			price: item.price,
			preloader: item.preloader
		},
		box: {
			uuid: box.uuid,
			name: box.name,
			price: box.price
		}
	}
	dropInfoModal.value = true
}
</script>
